import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import logger from '../utils/logger';

/**
 * Formats a datetime string in the specified timezone
 * @param {string} dateTimeString - ISO datetime string
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {string} Formatted datetime string
 */
export const formatBookingDateTime = (dateTimeString, timezone) => {
  if (!dateTimeString) return 'Invalid Date';
  
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Convert to the booking's timezone
    if (timezone) {
      try {
        const zonedDate = utcToZonedTime(date, timezone);
        return format(zonedDate, 'PPP p'); // e.g., "August 26th, 2025 at 2:30 PM"
      } catch (timezoneError) {
        // Fallback to local time if timezone conversion fails
        logger.warn('UTILS', 'Invalid timezone:', timezone, timezoneError);
        return format(date, 'PPP p');
      }
    }
    
    // Fallback to local time
    return format(date, 'PPP p');
  } catch (error) {
    logger.error('UTILS', 'Date formatting error:', { error: error });
    return 'Invalid Date';
  }
};

/**
 * Formats only the date part of a datetime string in the specified timezone
 * @param {string} dateTimeString - ISO datetime string
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Formatted date string
 */
export const formatBookingDate = (dateTimeString, timezone) => {
  if (!dateTimeString) return 'Invalid Date';

  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // IMPORTANT: Backend already converts datetime to user's timezone
    // The dateTimeString is ALREADY in the correct timezone
    // We just need to format it, NOT convert it again
    return format(date, 'PPP'); // e.g., "December 3rd, 2025"
  } catch (error) {
    logger.error('UTILS', 'Date formatting error:', { error: error });
    return 'Invalid Date';
  }
};

/**
 * Formats the time range for a booking in the specified timezone
 * @param {string} startDateTime - ISO datetime string for start time
 * @param {string} endDateTime - ISO datetime string for end time (optional)
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Formatted time range string
 */
export const formatBookingTime = (startDateTime, endDateTime, timezone) => {
  if (!startDateTime) return 'Invalid Time';

  try {
    const startDate = new Date(startDateTime);
    if (isNaN(startDate.getTime())) {
      return 'Invalid Time';
    }

    // IMPORTANT: Backend already converts datetime to user's timezone
    // The dateTimeString is ALREADY in the correct timezone
    // We just need to format it, NOT convert it again
    const startTime = format(startDate, 'p'); // e.g., "3:00 PM"

    // If no end time, return just start time
    if (!endDateTime) {
      return startTime;
    }

    const endDate = new Date(endDateTime);
    if (isNaN(endDate.getTime())) {
      return startTime; // Return start time if end time is invalid
    }

    const endTime = format(endDate, 'p'); // e.g., "4:00 PM"

    return `${startTime} - ${endTime}`;
  } catch (error) {
    logger.error('UTILS', 'Time formatting error:', { error: error });
    return 'Invalid Time';
  }
};